import { useState, useEffect, useRef } from "react";
import { motion, useDragControls, useMotionValue } from "framer-motion";
import {
  X,
  User,
  Mail,
  Phone,
  CalendarDays,
  Clock,
  FileText,
  AlertTriangle,
} from "lucide-react";
import useReservations from "../../../api/reservation";
import { useTheme } from "../../../context/ThemeContext";
import {
  format,
  parseISO,
  getDay,
  addMinutes,
  isSameDay,
  addDays,
} from "date-fns";
import { nl } from "date-fns/locale";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("nl", nl);

const ReservationModal = ({
  reservation,
  onClose,
  onSave,
  getBookedTimeSlots,
  isTimeSlotAvailable,
  settings,
}) => {
  const { createReservation, updateReservation } = useReservations();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );
  const sheetRef = useRef(null);
  const sheetContentRef = useRef(null);
  const sheetDragControls = useDragControls();
  const sheetY = useMotionValue(0);
  const isSheetScrolledToTop = useRef(true);
  const sheetTouchStartY = useRef(null);
  const sheetDragging = useRef(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Attach native touch listeners for pull-down-to-dismiss from anywhere in the content
  useEffect(() => {
    isSheetScrolledToTop.current = true;
    sheetTouchStartY.current = null;
    sheetDragging.current = false;

    const el = sheetContentRef.current;
    if (!el || !isMobile) return;

    const onScroll = () => {
      isSheetScrolledToTop.current = el.scrollTop <= 0;
    };
    const onTouchStart = (e) => {
      sheetTouchStartY.current = e.touches[0].clientY;
      sheetDragging.current = false;
    };
    const onTouchMove = (e) => {
      if (sheetDragging.current) {
        e.preventDefault();
        return;
      }
      if (sheetTouchStartY.current === null) return;
      const deltaY = e.touches[0].clientY - sheetTouchStartY.current;
      if (deltaY > 8 && isSheetScrolledToTop.current) {
        sheetDragging.current = true;
        e.preventDefault();
        const t = e.touches[0];
        sheetDragControls.start(
          new PointerEvent("pointerdown", {
            clientX: t.clientX,
            clientY: t.clientY,
            pointerId: 1,
            pointerType: "touch",
            bubbles: true,
          }),
        );
      }
    };
    const onTouchEnd = () => {
      sheetTouchStartY.current = null;
      sheetDragging.current = false;
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [isMobile, sheetDragControls]);

  const [formData, setFormData] = useState(() => {
    if (reservation) {
      // Format the reservation date to yyyy-MM-dd for the date input
      return {
        ...reservation,
        reservation_date: format(
          parseISO(reservation.reservation_date),
          "yyyy-MM-dd",
        ),
      };
    }
    return {
      customer_name: "",
      customer_email: "",
      customer_phone: "",
      reservation_date: format(new Date(), "yyyy-MM-dd"),
      start_time: "09:00",
      end_time: "10:00",
      service_type: "",
      notes: "",
      status: "pending",
    };
  });
  const [conflictError, setConflictError] = useState(null);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [availableStartTimes, setAvailableStartTimes] = useState([]);
  const [availableEndTimes, setAvailableEndTimes] = useState([]);
  const [usingSlotsMode, setUsingSlotsMode] = useState(false);

  // Calculate available start times
  useEffect(() => {
    if (!formData.reservation_date || !settings) {
      setAvailableStartTimes([]);
      return;
    }

    const date = parseISO(formData.reservation_date);
    const dayOfWeek = getDay(date);
    const dateStr = format(date, "yyyy-MM-dd");

    let config = settings.standard[dayOfWeek];
    if (settings.exceptions && settings.exceptions[dateStr]) {
      config = settings.exceptions[dateStr];
    }

    if (!config || !config.enabled) {
      setAvailableStartTimes([]);
      return;
    }

    const options = [];
    const toMinutes = (time) => {
      if (!time) return 0;
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m;
    };

    // If specific slots are defined, use them
    if (config.slots && config.slots.length > 0) {
      setUsingSlotsMode(true);
      config.slots.forEach((slot) => {
        // Check if slot is booked
        let isBlocked = false;
        const slotStart = toMinutes(slot.start);
        const slotEnd = toMinutes(slot.end);

        for (const booked of bookedSlots) {
          const bookedStart = toMinutes(booked.start);
          const bookedEnd = toMinutes(booked.end);
          // Overlap check
          if (slotStart < bookedEnd && slotEnd > bookedStart) {
            isBlocked = true;
            break;
          }
        }

        if (!isBlocked) {
          options.push(slot.start);
        }
      });
    } else {
      setUsingSlotsMode(false);
      // Fallback to general hours
      const start = config.generalStart || "09:00";
      const end = config.generalEnd || "17:00";
      const endMinutes = toMinutes(end);

      let currentTime = start;
      while (toMinutes(currentTime) < endMinutes) {
        const currentStartMinutes = toMinutes(currentTime);
        // Assume 15 min minimum slot for checking availability
        const currentEndMinutes = currentStartMinutes + 15;

        let isBlocked = false;
        for (const booked of bookedSlots) {
          const bookedStart = toMinutes(booked.start);
          const bookedEnd = toMinutes(booked.end);
          if (
            currentStartMinutes < bookedEnd &&
            currentEndMinutes > bookedStart
          ) {
            isBlocked = true;
            break;
          }
        }

        if (!isBlocked) {
          options.push(currentTime);
        }

        const [h, m] = currentTime.split(":").map(Number);
        const d = new Date();
        d.setHours(h, m, 0, 0);
        const next = addMinutes(d, 15);
        currentTime = format(next, "HH:mm");
      }
    }

    setAvailableStartTimes([...new Set(options)].sort());
  }, [formData.reservation_date, settings, bookedSlots]);

  // Calculate available end times based on start time
  useEffect(() => {
    if (!formData.start_time || !formData.reservation_date || !settings) {
      setAvailableEndTimes([]);
      return;
    }

    const date = parseISO(formData.reservation_date);
    const dayOfWeek = getDay(date);
    const dateStr = format(date, "yyyy-MM-dd");

    let config = settings.standard[dayOfWeek];
    if (settings.exceptions && settings.exceptions[dateStr]) {
      config = settings.exceptions[dateStr];
    }

    if (!config || !config.enabled) {
      setAvailableEndTimes([]);
      return;
    }

    const options = [];
    const toMinutes = (time) => {
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m;
    };

    // If using slots, find the slot that matches start time
    if (config.slots && config.slots.length > 0) {
      const slot = config.slots.find((s) => s.start === formData.start_time);
      if (slot) {
        options.push(slot.end);
      }
    } else {
      // General hours
      const generalEnd = config.generalEnd || "17:00";
      const endMinutes = toMinutes(generalEnd);

      let currentTime = formData.start_time;
      // Start offering end times from start + 15 mins
      const [h, m] = currentTime.split(":").map(Number);
      const d = new Date();
      d.setHours(h, m, 0, 0);
      let next = addMinutes(d, 15);
      currentTime = format(next, "HH:mm");

      while (toMinutes(currentTime) <= endMinutes) {
        options.push(currentTime);
        const [h, m] = currentTime.split(":").map(Number);
        const d = new Date();
        d.setHours(h, m, 0, 0);
        const next = addMinutes(d, 15);
        currentTime = format(next, "HH:mm");
      }
    }

    setAvailableEndTimes(options);
  }, [formData.start_time, formData.reservation_date, settings]);

  const handleStartTimeChange = (newStartTime) => {
    let newEndTime = formData.end_time;

    if (settings && formData.reservation_date) {
      const date = parseISO(formData.reservation_date);
      const dayOfWeek = getDay(date);
      const dateStr = format(date, "yyyy-MM-dd");

      let config = settings.standard[dayOfWeek];
      if (settings.exceptions && settings.exceptions[dateStr]) {
        config = settings.exceptions[dateStr];
      }

      // If using slots, auto-select the end time
      if (config && config.slots && config.slots.length > 0) {
        const slot = config.slots.find((s) => s.start === newStartTime);
        if (slot) {
          newEndTime = slot.end;
        }
      }
    }

    setFormData({
      ...formData,
      start_time: newStartTime,
      end_time: newEndTime,
    });
  };

  // Update booked slots when date changes
  useEffect(() => {
    if (formData.reservation_date && getBookedTimeSlots) {
      const slots = getBookedTimeSlots(
        parseISO(formData.reservation_date),
        reservation?.id,
      );
      setBookedSlots(slots);
    }
  }, [formData.reservation_date, getBookedTimeSlots, reservation]);

  // Check time slot availability when times change
  useEffect(() => {
    if (
      formData.start_time &&
      formData.end_time &&
      formData.reservation_date &&
      isTimeSlotAvailable
    ) {
      const result = isTimeSlotAvailable(
        parseISO(formData.reservation_date),
        formData.start_time,
        formData.end_time,
        reservation?.id,
      );

      if (!result.available) {
        setConflictError(
          `Er is al een afspraak met ${result.conflictWith} op dit tijdstip`,
        );
      } else {
        setConflictError(null);
      }
    }
  }, [
    formData.start_time,
    formData.end_time,
    formData.reservation_date,
    isTimeSlotAvailable,
    reservation,
  ]);

  const isTimeDisabled = (time) => {
    if (!bookedSlots.length) return false;

    const checkTime = time;
    for (const slot of bookedSlots) {
      if (checkTime >= slot.start && checkTime < slot.end) {
        return true;
      }
    }
    return false;
  };

  const isDateEnabled = (date) => {
    if (!settings) return true;

    const dayOfWeek = getDay(date);
    const dateStr = format(date, "yyyy-MM-dd");

    // Check if there's an exception for this specific date
    if (settings.exceptions && settings.exceptions[dateStr]) {
      return settings.exceptions[dateStr].enabled;
    }

    // Check standard day configuration
    if (settings.standard && settings.standard[dayOfWeek]) {
      return settings.standard[dayOfWeek].enabled;
    }

    // Default to disabled if no configuration exists
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate time slot before submitting
    if (conflictError) {
      alert(conflictError);
      return;
    }

    try {
      if (reservation) {
        await updateReservation(reservation.id, formData);
      } else {
        await createReservation(formData);
      }
      onSave();
    } catch (error) {
      console.error("Error saving reservation:", error);
      if (error.response?.data?.message?.includes("overlaps")) {
        alert(
          "Er is al een afspraak op dit tijdstip. Kies een ander tijdstip.",
        );
      } else {
        alert("Fout bij opslaan reservering");
      }
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex ${isMobile ? "items-end" : "items-center"} justify-center z-50`}
      onClick={isMobile ? undefined : onClose}
    >
      <motion.div
        ref={sheetRef}
        initial={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
        animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
        exit={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
        transition={
          isMobile
            ? { type: "spring", damping: 35, stiffness: 400 }
            : { type: "spring", duration: 0.3 }
        }
        drag={isMobile ? "y" : false}
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.6 }}
        dragListener={false}
        dragControls={sheetDragControls}
        onDragEnd={(_, info) => {
          if (info.offset.y > 80 || info.velocity.y > 500) {
            onClose();
          }
        }}
        style={
          isMobile
            ? {
                y: sheetY,
                borderTopLeftRadius: "1rem",
                borderTopRightRadius: "1rem",
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            : undefined
        }
        onClick={(e) => e.stopPropagation()}
        className={`w-full border ${isMobile ? "max-w-full max-h-[85vh] flex flex-col" : "rounded-xl max-w-2xl max-h-[90vh] overflow-y-auto"} ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
      >
        {/* Mobile drag handle */}
        {isMobile && (
          <div
            onPointerDown={(e) => sheetDragControls.start(e)}
            className="flex justify-center pt-3 pb-1 shrink-0 cursor-grab active:cursor-grabbing touch-none"
            style={{ touchAction: "none" }}
          >
            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
          </div>
        )}
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
        >
          <h2
            className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
          >
            {reservation ? "Reservering Bewerken" : "Nieuwe Reservering"}
          </h2>
          <button
            onClick={onClose}
            className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className={
            isMobile
              ? "p-6 space-y-5 overflow-y-auto overscroll-contain flex-1"
              : "p-6 space-y-5"
          }
          ref={isMobile ? sheetContentRef : undefined}
        >
          {/* Contact Section */}
          <div className="space-y-3">
            <h3
              className={`text-[12px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Klantgegevens
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label
                  className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Naam *
                </label>
                <div className="relative">
                  <User
                    className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <input
                    type="text"
                    required
                    value={formData.customer_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customer_name: e.target.value,
                      })
                    }
                    className={`w-full h-9 pl-9 pr-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"}`}
                    placeholder="Volledige naam"
                  />
                </div>
              </div>
              <div>
                <label
                  className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Email *
                </label>
                <div className="relative">
                  <Mail
                    className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <input
                    type="email"
                    required
                    value={formData.customer_email}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customer_email: e.target.value,
                      })
                    }
                    className={`w-full h-9 pl-9 pr-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"}`}
                    placeholder="email@voorbeeld.be"
                  />
                </div>
              </div>
              <div>
                <label
                  className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Telefoon
                </label>
                <div className="relative">
                  <Phone
                    className={`absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        customer_phone: e.target.value,
                      })
                    }
                    className={`w-full h-9 pl-9 pr-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"}`}
                    placeholder="+32 ..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Date & Time Section */}
          <div className="space-y-3">
            <h3
              className={`text-[12px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Datum & Tijd
            </h3>

            <div>
              <label
                className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Datum *
              </label>
              <div className="w-full">
                <DatePicker
                  selected={parseISO(formData.reservation_date)}
                  onChange={(date) =>
                    setFormData({
                      ...formData,
                      reservation_date: format(date, "yyyy-MM-dd"),
                    })
                  }
                  minDate={new Date()}
                  filterDate={isDateEnabled}
                  dateFormat="dd/MM/yyyy"
                  locale="nl"
                  className={`w-full h-9 px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                  wrapperClassName="w-full"
                />
              </div>
            </div>

            {/* Booked slots warning */}
            {bookedSlots.length > 0 && (
              <div
                className={`rounded-xl p-3 border ${isDark ? "bg-yellow-500/10 border-yellow-500/20" : "bg-yellow-50 border-yellow-100"}`}
              >
                <p
                  className={`text-[12px] font-medium mb-1.5 flex items-center gap-1.5 ${isDark ? "text-yellow-400" : "text-yellow-700"}`}
                >
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Bezette tijden op{" "}
                  {format(parseISO(formData.reservation_date), "dd/MM/yyyy", {
                    locale: nl,
                  })}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {bookedSlots.map((slot, idx) => (
                    <span
                      key={idx}
                      className={`text-[11px] px-2 py-0.5 rounded-md font-medium ${isDark ? "bg-yellow-500/15 text-yellow-400" : "bg-yellow-100 text-yellow-700"}`}
                    >
                      {slot.start.slice(0, 5)} – {slot.end.slice(0, 5)} (
                      {slot.customerName})
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Starttijd *
                </label>
                <select
                  required
                  value={formData.start_time}
                  onChange={(e) => handleStartTimeChange(e.target.value)}
                  className={`w-full h-9 px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                >
                  {availableStartTimes.length === 0 && (
                    <option value="" disabled>
                      Geen tijden beschikbaar
                    </option>
                  )}
                  {availableStartTimes.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Eindtijd *
                </label>
                {usingSlotsMode ? (
                  <div
                    className={`h-9 flex items-center px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.02] border-white/[0.06] text-gray-400" : "bg-gray-100 border-gray-200 text-gray-700"}`}
                  >
                    {formData.end_time}
                  </div>
                ) : (
                  <select
                    required
                    value={formData.end_time}
                    onChange={(e) =>
                      setFormData({ ...formData, end_time: e.target.value })
                    }
                    className={`w-full h-9 px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                  >
                    {availableEndTimes.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                )}
                {usingSlotsMode && (
                  <p
                    className={`text-[11px] mt-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    Automatisch bepaald door het gekozen tijdslot
                  </p>
                )}
              </div>
            </div>

            {/* Conflict Error */}
            {conflictError && (
              <div
                className={`rounded-xl p-3 border ${isDark ? "bg-red-500/10 border-red-500/20" : "bg-red-50 border-red-100"}`}
              >
                <p
                  className={`text-[12px] font-medium ${isDark ? "text-red-400" : "text-red-700"}`}
                >
                  {conflictError}
                </p>
                <p
                  className={`text-[11px] mt-0.5 ${isDark ? "text-red-400/70" : "text-red-500"}`}
                >
                  Kies een ander tijdstip of deblokkeer het bestaande tijdslot.
                </p>
              </div>
            )}
          </div>

          {/* Details Section */}
          <div className="space-y-3">
            <h3
              className={`text-[12px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
            >
              Extra Details
            </h3>
            <div>
              <label
                className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Service Type
              </label>
              <input
                type="text"
                value={formData.service_type}
                onChange={(e) =>
                  setFormData({ ...formData, service_type: e.target.value })
                }
                placeholder="bijv. Consultatie, Meeting, etc."
                className={`w-full h-9 px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"}`}
              />
            </div>
            <div>
              <label
                className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Notities
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
                rows={3}
                placeholder="Optionele opmerkingen..."
                className={`w-full px-3 py-2 rounded-lg text-[13px] border resize-none ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"}`}
              />
            </div>

            {/* Status (edit mode only) */}
            {reservation && (
              <div>
                <label
                  className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                >
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className={`w-full h-9 px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                >
                  <option value="pending">In afwachting</option>
                  <option value="confirmed">Bevestigd</option>
                  <option value="cancelled">Geannuleerd</option>
                  <option value="completed">Voltooid</option>
                </select>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className={`flex gap-3 pt-4 border-t ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
          >
            <button
              type="submit"
              className={`flex-1 h-9 rounded-lg text-[13px] font-medium transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
            >
              {reservation ? "Opslaan" : "Aanmaken"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 h-9 rounded-lg text-[13px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              Annuleren
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ReservationModal;
